import React from 'react';
import { Row, Col, Statistic, Table, Tag, Empty, Alert, Typography } from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  useGetAnalyticsOverviewQuery,
  useListFeatureFlagsQuery,
  useListCategoriesQuery,
} from '../services/adminApi';
import AdminPageHeader, { adminPanelStyle } from './AdminPageHeader';

const { Text, Paragraph } = Typography;

export default function SettingsPage() {
  const navigate = useNavigate();
  const { data: overview, isLoading: overviewLoading, isError, error } = useGetAnalyticsOverviewQuery();
  const { data: flags, isLoading: flagsLoading } = useListFeatureFlagsQuery();
  const { data: categories, isLoading: categoriesLoading } = useListCategoriesQuery();

  const categoryColumns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Slug', dataIndex: 'slug', key: 'slug' },
    {
      title: 'Depth',
      dataIndex: 'depth',
      key: 'depth',
      width: 80,
      render: (d) => <Tag>{d}</Tag>,
    },
    {
      title: 'Parent',
      dataIndex: 'parent_id',
      key: 'parent_id',
      width: 90,
      render: (id) => (id == null ? '—' : id),
    },
  ];

  return (
    <div>
      <AdminPageHeader
        title="Settings"
        subtitle="Platform overview, catalog categories, and launch flags"
      />

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={error?.data?.title || 'Failed to load settings data'}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {[
          { label: 'Deals', value: overview?.totalDeals },
          { label: 'Users', value: overview?.activeUsers },
          { label: 'Merchants', value: overview?.totalMerchants },
          { label: 'Pending review', value: overview?.pendingReview },
        ].map((card) => (
          <Col xs={24} sm={12} lg={6} key={card.label}>
            <div style={{ ...adminPanelStyle, padding: 18 }}>
              <Text style={{ fontSize: 12, fontWeight: 600, color: 'var(--df-text-secondary)' }}>
                {card.label}
              </Text>
              <Statistic
                value={overviewLoading ? '—' : (card.value || 0)}
                valueStyle={{
                  fontSize: 26,
                  fontWeight: 700,
                  fontFamily: 'var(--df-font-display)',
                  color: 'var(--df-text)',
                }}
              />
            </div>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24}>
          <div style={{ ...adminPanelStyle, padding: 18 }}>
            <Text strong style={{ fontFamily: 'var(--df-font-display)', fontSize: 15, display: 'block', marginBottom: 8 }}>
              System notifications
            </Text>
            <Paragraph style={{ color: 'var(--df-text-secondary)', marginBottom: 12 }}>
              In-app broadcasts create real
              {' '}
              <Text code>notifications</Text>
              {' '}
              rows for the selected audience. Email/push is gated by the
              {' '}
              <Text code>email_notifications</Text>
              {' '}
              feature flag and SMTP — this console does not fake those channels.
            </Paragraph>
            <button
              type="button"
              onClick={() => navigate('/admin/notifications')}
              style={{
                border: '1px solid var(--df-border)',
                background: 'var(--df-surface)',
                color: 'var(--df-primary)',
                fontWeight: 600,
                cursor: 'pointer',
                borderRadius: 8,
                padding: '8px 14px',
              }}
            >
              Open notification broadcast
            </button>
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <div style={{ ...adminPanelStyle, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <Text strong style={{ fontFamily: 'var(--df-font-display)', fontSize: 15 }}>
                Feature flags
              </Text>
              <button
                type="button"
                onClick={() => navigate('/admin/feature-flags')}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--df-primary)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Manage
              </button>
            </div>
            <Table
              dataSource={flags?.items}
              loading={flagsLoading}
              rowKey="key"
              size="small"
              pagination={false}
              locale={{ emptyText: <Empty description="No flags" /> }}
              columns={[
                { title: 'Key', dataIndex: 'key', key: 'key', ellipsis: true },
                {
                  title: 'On',
                  dataIndex: 'enabled',
                  key: 'enabled',
                  width: 70,
                  render: (v) => (v ? <Tag color="green">yes</Tag> : <Tag>no</Tag>),
                },
              ]}
            />
          </div>
        </Col>
        <Col xs={24} lg={12}>
          <div style={{ ...adminPanelStyle, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
              <Text strong style={{ fontFamily: 'var(--df-font-display)', fontSize: 15 }}>
                Categories
              </Text>
              <button
                type="button"
                onClick={() => navigate('/admin/categories')}
                style={{
                  border: 'none',
                  background: 'transparent',
                  color: 'var(--df-primary)',
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Manage
              </button>
            </div>
            <Table
              dataSource={categories?.items}
              loading={categoriesLoading}
              rowKey="id"
              size="small"
              pagination={{ pageSize: 8 }}
              locale={{ emptyText: <Empty description="No categories" /> }}
              columns={categoryColumns}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
}
